import React, { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useCheckoutSession } from '@/hooks/use-checkout-session';
import { useCartContext } from '@/hooks/use-cart-context';
import { Button } from '@/components/form';
import { safeHref } from '@/utils/url-utils';
import { useLocale } from '@/hooks/use-locale';
import { FLAGS } from '@/services/session';

export function SubscriptionDetails ({
  className,
  onAgreementChange,
  agreed = false,
}: SubscriptionDetailsProps) {
  // Contexts and hooks
  const [checkoutSession] = useCheckoutSession();
  const { pricing } = useCartContext();
  const { locale, t } = useLocale();

  // Refs and state
  const hasShownContent = useRef(false);

  // Derived values
  const subscriptions = checkoutSession?.cart?.subscriptions || [];
  const currency = checkoutSession?.cart?.currency?.code || 'USD';
  const flags = checkoutSession?.flags || [];
  const siteFlags = checkoutSession?.site?.flags || [];
  const taxEnabled = siteFlags.includes(FLAGS.TAX_ENABLED);
  const selectionsEnabled = flags.includes(FLAGS.DISPLAY_SUBSCRIPTIONS_AS_OPTIONS);
  const subscriptionsToShow = selectionsEnabled
    ? subscriptions.filter(sub => Number(sub.quantity) > 0)
    : subscriptions.filter(sub => sub.quantity === '' || Number(sub.quantity) > 0);

  // UI constants
  const buttonClass = twMerge(
    'w-auto h-12 min-h-[48px] gap-3 rounded-md pt-[6px] pb-[6px] px-6',
    agreed ? 'bg-gray-400 hover:bg-gray-400 active:bg-gray-400 text-white cursor-not-allowed pointer-events-none' : ''
  );

  const svgCheckIcon = () => (
    <svg style={{ display: 'inline-block', marginRight: '0.3rem', marginBottom: '0.2rem' }} width="20"
      height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.2999 10C19.2999 4.90001 15.1 0.700012 9.99995 0.700012C4.86245 0.700012 0.699951 4.90001
      0.699951 10C0.699951 15.1375 4.86245 19.3 9.99995 19.3C15.1 19.3 19.2999 15.1375 19.2999 10ZM8.91245
      14.95C8.68745 15.175 8.27495 15.175 8.04995 14.95L4.14995 11.05C3.92495 10.825 3.92495 10.4125 4.14995
      10.1875L5.01245 9.36251C5.23745 9.10001 5.61245 9.10001 5.83745 9.36251L8.49995 11.9875L14.125
      6.36251C14.35 6.10001 14.725 6.10001 14.95 6.36251L15.8125 7.18751C16.0374 7.41251 16.0374 7.82501
      15.8125 8.05001L8.91245 14.95Z" fill="#737169"/>
    </svg>
  );

  // Utility functions
  function formatDate (date: string | Date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  function formatCurrency (amount: number, currency?: string) {
    if (!currency) return `${amount}`;

    const nativeMin = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).resolvedOptions().minimumFractionDigits;

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: nativeMin,
      maximumFractionDigits: 9,
    }).format(amount);
  }

  function normalizeInterval (length: number, unit: string) {
    if (unit === 'months' && length % 12 === 0) return { length: length / 12, unit: 'years' };
    return { length, unit };
  }

  function formatIntervalLabel (length: number, unit: string) {
    const { length: l, unit: u } = normalizeInterval(length, unit);
    return `${l > 1 ? `${l} ` : ''}${t(`pluralize.${u}`, { count: l })}`;
  }

  function addIntervalToDate (
    startDate: Date,
    intervalLength: number,
    intervalUnit: string
  ): Date {
    const date = new Date(startDate);
    const unit = intervalUnit.toLowerCase();

    if (unit.startsWith('day')) {
      date.setDate(date.getDate() + intervalLength);
    } else if (unit.startsWith('week')) {
      date.setDate(date.getDate() + intervalLength * 7);
    } else if (unit.startsWith('month')) {
      let month = date.getMonth();
      let newNumberOfMonth = month + intervalLength;
      const year = date.getFullYear();
      const lastDayMonth = new Date(year, month + 1, 0).getDate();

      if (lastDayMonth === date.getDate()) {
        date.setMonth(newNumberOfMonth + 1, 0);
      } else {
        date.setMonth(newNumberOfMonth);
      }
    } else if (unit.startsWith('year')) {
      date.setFullYear(date.getFullYear() + intervalLength);
    }
    return date;
  }

  function calculateTrialEndsAt (trialLength: number, trialUnit: string): Date | null {
    if (!trialLength || !trialUnit) return null;
    return addIntervalToDate(new Date(), trialLength, trialUnit);
  }

  function getBillingStartDate (trialEndsAt: Date | null): Date {
    return trialEndsAt ? new Date(trialEndsAt) : new Date();
  }

  function calculateBillingEndDate (
    billingStartDate: Date,
    intervalUnit: string,
    totalCycles: number
  ): Date | null {
    if (!billingStartDate || !intervalUnit || totalCycles <= 0) return null;
    return addIntervalToDate(billingStartDate, totalCycles, intervalUnit);
  }

  function getPlanData (sub: any) {
    const pricingSub = (pricing as any)?.items?.subscriptions?.find(
      (s: any) => s.id === sub.ephemeralId
    );
    if (pricingSub?.items?.plan) return pricingSub.items.plan;

    return null;
  }

  function getAddOnChargeSummaries (plan: any, sub: any, currency: string) {
    if (!plan.addons?.length) return null;
    const format = (amt: number) => formatCurrency(amt, currency);

    return plan.addons.flatMap((addon: any) => {
      const selected = sub.addOns?.find((a: any) => a.code === addon.code);
      const qty = Number(selected?.quantity);
      if (!qty) return [];

      const getTierPrice = (tier: any) => {
        const c = tier.currencies?.find((c: any) => c.currency_code === currency);
        return parseFloat(c?.unit_amount_decimal) || c?.unit_amount || 0;
      };

      let total = 0, key = '', vars: any = {};

      if (addon.tier_type === 'flat') {
        const price = parseFloat(addon.price?.[currency]?.unit_amount_decimal)
                   || addon.price?.[currency]?.unit_amount || 0;
        if (price) {
          total = price * qty;
          if (qty === 1) {
            key = 'subscription.details.addons.flat.charge';
            vars = { amount: format(price), addOnName: addon.name };
          } else {
            key = 'subscription.details.addons.no-flat.charge';
            vars = { amount: format(total), unitsLabel: 'units', units: qty, addOnName: addon.name };
          }
        }
      } else if (addon.tier_type === 'tiered') {
        let unitsLeft = qty;
        total = addon.tiers.reduce((sum: number, tier: any) => {
          if (!unitsLeft) return sum;
          const tierPrice = getTierPrice(tier);
          const tierUnits = Math.min(unitsLeft, tier.ending_quantity - (tier.starting_quantity ?? 0));
          unitsLeft -= tierUnits;
          return sum + tierPrice * tierUnits;
        }, 0);
        key = 'subscription.details.addons.no-flat.charge';
        vars = {
          amount: format(total),
          unitsLabel: qty === 1 ? 'unit' : 'units',
          units: qty,
          addOnName: addon.name
        };
      } else if (addon.tier_type === 'volume' || addon.tier_type === 'stairstep') {
        const tier = addon.tiers.find((t: any) => qty <= t.ending_quantity);
        const tierPrice = getTierPrice(tier);
        total = addon.tier_type === 'volume' ? tierPrice * qty : tierPrice;
        key = 'subscription.details.addons.no-flat.charge';
        vars = {
          amount: format(total),
          unitsLabel: qty === 1 ? 'unit' : 'units',
          units: qty,
          addOnName: addon.name
        };
      }

      return total > 0 ? [{ name: addon.name, total, units: qty, key, variables: vars }] : [];
    });
  }

  function renderTranslatedListItem (key: string, variables: {}) {
    // Generate a unique textCode using key, variables, and a random value
    const textCode = `${key}-${JSON.stringify(variables)}-${Math.random().toString(36).substr(2, 9)}`;
    return <li key={textCode}>{t(key, variables)}</li>;
  }

  function link (text: string, url: string) {
    return (
      <a href={safeHref(url)} className="underline hover:text-gray-700" target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    );
  }

  function onClickButton () {
    if (!agreed && onAgreementChange) {
      onAgreementChange();
    }
  }

  useEffect(() => {
    if (subscriptionsToShow.length > 0) {
      hasShownContent.current = true;
    }
  }, [subscriptionsToShow.length]);

  const shouldRender = subscriptionsToShow.length > 0 || hasShownContent.current;
  if (!shouldRender) return null;

  return (
    <div
      className={twMerge('p-0 rounded-none bg-transparent', className)}
      data-testid="subscription-details-section">
      <div className="mt-[14px]" />
      {subscriptionsToShow.map((sub: any, idx: number) => {
        const plan = getPlanData(sub);
        if (!plan) return null;
        const planName = plan.name;
        const trialLength = plan.trial_interval_length || plan.trial?.length;
        const trialUnit = plan.trial_interval_unit || plan.trial?.interval;
        const setupFee = plan.price[currency]?.setup_fee || 0;
        const period = plan.period || {};
        const intervalUnit = period.interval || 'months';
        const intervalLength = period.length || 1;
        const totalCycles = plan.total_billing_cycles || 0;
        const quantity = Number(sub.quantity) || 1;
        const unitAmount = (plan.price[currency]?.unit_amount || 0) * quantity;
        const rampIntervals = plan.ramp_intervals || [];
        const reminderDays = checkoutSession.site?.configuration?.prerenewalReminderDays || 0;
        const trialEndsAt = calculateTrialEndsAt(trialLength, trialUnit);
        const billingStartDate = getBillingStartDate(trialEndsAt);
        const billingEndsAt = calculateBillingEndDate(billingStartDate, intervalUnit, intervalLength * totalCycles);
        const addOnsSummary = getAddOnChargeSummaries(plan, sub, currency);
        const intervalLengthLabel = formatIntervalLabel(intervalLength, intervalUnit);
        const showTaxLabel = taxEnabled && !plan.tax_exempt;

        return (
          <div key={plan.code || idx} className="mb-4 p-4 border border-gray-300 rounded-lg">
            <div className="font-bold text-lg mb-2">{planName}</div>
            <div className="space-y-2 text-sm">
              <ul className='list-disc list-inside'>
                {/* 1. Free trial */}
                {trialLength && trialUnit && renderTranslatedListItem('subscription.details.trial.start', {
                  trialLength: trialLength,
                  trialInterval: t(`pluralize.${trialUnit}`, { count: trialLength })
                })}
                {/* 2. Cancel before trial end */}
                {trialEndsAt && renderTranslatedListItem('subscription.details.trial.ends', {
                  trialEndsAt: formatDate(trialEndsAt)
                })}
                {/* 3. Starting billing (with setup fee if present) */}
                {setupFee > 0 ?
                  renderTranslatedListItem('subscription.details.billing.starting.setupfee', {
                    startDate: formatDate(billingStartDate!),
                    amount: formatCurrency(unitAmount, currency),
                    intervalLengthLabel: intervalLengthLabel,
                    setupFee: formatCurrency(setupFee, currency),
                    taxable: showTaxLabel
                  }) : renderTranslatedListItem('subscription.details.billing.starting', {
                    startDate: formatDate(billingStartDate!),
                    amount: formatCurrency(unitAmount, currency),
                    intervalLengthLabel: intervalLengthLabel,
                    taxable: showTaxLabel
                  })
                }
                {/* 5. Ramps as sub-bullets under renewal (skip first ramp) */}
                {rampIntervals.length > 1 && (
                  <div className="ml-4 mt-1">
                    {rampIntervals.slice(1).map((ramp: any) => {
                      const priceObj = ramp.prices?.find((p: any) => p.currency_code === currency);
                      if (!priceObj) return null;
                      const start = (ramp.starting_billing_cycle - 1) * intervalLength;
                      if (start <= 0) return null;
                      const { length: startLength, unit: startUnit } = normalizeInterval(start, intervalUnit);
                      const afterChargeTime = `${startLength} ${t(`pluralize.${startUnit}`, { count: startLength })}`;
                      const newChargeAmount = `${formatCurrency(priceObj.unit_amount * quantity, currency)}/${formatIntervalLabel(intervalLength, intervalUnit)}`;

                      return renderTranslatedListItem('subscription.details.ramp.after', {
                        afterChargeTime: afterChargeTime,
                        newChargeAmount: newChargeAmount
                      });
                    })}
                  </div>
                )}
                {/* 4. Subscription renews automatically */}
                {typeof plan.auto_renew === 'boolean' && (
                  plan.auto_renew
                    ? renderTranslatedListItem(
                      'subscription.details.renewal',
                      { totalCyclesLengthLabel: intervalLengthLabel }
                    ) : renderTranslatedListItem(
                      'subscription.details.no-renewal',
                      { billingEndsAt: formatDate(billingEndsAt!) }
                    )
                )}
                {/* 6. Add-ons summary */}
                {addOnsSummary && addOnsSummary.length > 0 && addOnsSummary.map((addOnSummary: any) =>
                  renderTranslatedListItem(addOnSummary.key, addOnSummary.variables)
                )}
                {/* 7. Email reminder (dynamic from site configuration) */}
                {trialLength && trialUnit && typeof reminderDays === 'number' &&
                  renderTranslatedListItem('subscription.details.email.reminder', { days: reminderDays })
                }
              </ul>
            </div>
            <div className="mt-4 text-sm text-gray-500">
              {((checkoutSession?.cancelUrl || checkoutSession?.supportUrl)) && (
                <>
                  {t('subscription.changeOrCancel')}
                  {checkoutSession?.cancelUrl && link(t('subscription.access-account'), checkoutSession?.cancelUrl)}
                  {checkoutSession?.cancelUrl && checkoutSession?.supportUrl && ' or '}
                  {checkoutSession?.supportUrl && link(t('subscription.contact-support'), checkoutSession.supportUrl)}
                </>
              )}
            </div>
          </div>
        );
      })}
      <div className="mb-4 text-sm text-checkout-subtle">
        {t('subscription.agreement.text')}
      </div>
      <div className="mt-4">
        <Button
          variant="primary"
          disabled={false}
          className={buttonClass}
          onClick={onClickButton}
          data-testid="subscription-details-continue"
        >
          {agreed && svgCheckIcon()}
          {t('subscription.agreement.button')}
        </Button>
      </div>
    </div>
  );
}

interface SubscriptionDetailsProps {
  className?: string;
  onAgreementChange?: () => void;
  agreed?: boolean;
}
